import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Modal } from "antd";
import i18next from "i18next";
import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

export const NOTIFICATION_MODE = {
  EMPTY: 'EMPTY',
  PROJECT_SOURCES: 'PROJECT_SOURCES',
};

export const FormsContext = createContext({
  onYesNotification: () => { },
  onNoNotification: () => { },
  notificationMode: NOTIFICATION_MODE.EMPTY,
  notificationState: {},
  setNotificationMode: () => { },
  changeNotificationState: () => { },
  authenticationLinks: [],
  setAuthenticationLinks: () => { },
});

export const onBeforeUnload = (event) => {
  // Display the warning message
  event.preventDefault();
  event.returnValue = 'Changes that you made may not be saved.';
};

export const FormsProvider = ({ children }) => {
  const [notificationMode, setNotificationMode] = useState(NOTIFICATION_MODE.EMPTY);
  const [notificationState, setNotificationState] = useState({ visible: false });
  const [authenticationLinks, setAuthenticationLinks] = useState([]);

  const onNoNotification = useCallback(() => {
    window.removeEventListener('beforeunload', onBeforeUnload);
    setNotificationState(() => ({ visible: false }));
  }, []);

  const changeNotificationState = useCallback((params) => {
    if (notificationMode === NOTIFICATION_MODE.PROJECT_SOURCES) {
      Modal.confirm({
        title: i18next.t('You have unsaved changes'),
        icon: <ExclamationCircleOutlined />,
        content: i18next.t('Are you sure you want to leave the page?'),
        okText: i18next.t('Yes'),
        cancelText: i18next.t('No'),
        onOk: () => {
          params.onYes();
          setNotificationMode(NOTIFICATION_MODE.EMPTY);
          onNoNotification();
        },
        onCancel: () => onNoNotification(),
      })
    }
  }, [onNoNotification, notificationMode]);

  const value = useMemo(() => ({
    onNoNotification,
    notificationMode,
    notificationState,
    setNotificationMode,
    changeNotificationState,
    authenticationLinks,
    setAuthenticationLinks,
  }), [onNoNotification, changeNotificationState, notificationMode, notificationState, authenticationLinks]);

  return (
    <FormsContext.Provider value={value}>
      {children}
    </FormsContext.Provider>
  )
};

export const useForms = () => {
  const value = useContext(FormsContext);
  return value;
}
